import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Clock, AlertTriangle, Percent, IndianRupee, User, Briefcase, FileText, ChevronDown, ChevronUp, Send, MessageSquare, Tag, FileSpreadsheet } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';

interface ConversationMessage {
  id: string;
  userId: string;
  userName: string;
  message: string;
  timestamp: string;
  createdAt?: string;
}

interface DiscountRequest {
  id: string;
  proposalId: string | null;
  salesOrderId: string | null;
  invoiceId: string | null;
  dealId: string | null;
  discountScope: string | null; // 'product' | 'document'
  documentType: string | null; // 'proposal' | 'sales_order' | 'invoice'
  itemDescription: string | null;
  itemRate: number | null;
  itemQuantity: number | null;
  maxDiscountPct: number | null;
  requestedPct: number;
  requestedAmt: number | null;
  dealValue: number | null;
  dealTitle: string | null;
  customerName: string | null;
  reason: string | null;
  status: string;
  remarks: string | null;
  createdAt: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  requestedBy: { id: string; firstName: string; lastName: string; email: string };
  approvedBy: { id: string; firstName: string; lastName: string } | null;
  product: { id: string; name: string; sku: string | null; maxDiscount: number | null } | null;
  deal: {
    id: string; title: string; value: number;
    contact: { firstName: string; lastName: string } | null;
    account: { name: string } | null;
    activities: { id: string; title: string; createdAt: string }[];
  } | null;
  conversation?: ConversationMessage[];
}

export function DiscountRequestsPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<DiscountRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('pending');
  const [scopeFilter, setScopeFilter] = useState<string>('');
  const [docTypeFilter, setDocTypeFilter] = useState<string>('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [remarksModal, setRemarksModal] = useState<{ id: string; action: 'approve' | 'reject' } | null>(null);
  const [remarksText, setRemarksText] = useState('');
  const [expandedConversation, setExpandedConversation] = useState<string | null>(null);
  const [conversationMessages, setConversationMessages] = useState<Record<string, ConversationMessage[]>>({});
  const [newMessage, setNewMessage] = useState<Record<string, string>>({});
  const [sendingMessage, setSendingMessage] = useState<string | null>(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filter) params.status = filter;
      if (scopeFilter) params.discountScope = scopeFilter;
      if (docTypeFilter) params.documentType = docTypeFilter;
      if (customerTypeFilter) params.customerType = customerTypeFilter;
      const res = await api.get('/discount-requests', { params });
      setRequests(res.data.data || []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [filter, scopeFilter, docTypeFilter, customerTypeFilter]);

  const loadConversation = async (requestId: string) => {
    try {
      const res = await api.get(`/discount-requests/${requestId}/conversation`);
      setConversationMessages(prev => ({ ...prev, [requestId]: res.data.data || [] }));
    } catch {
      setConversationMessages(prev => ({ ...prev, [requestId]: [] }));
    }
  };

  const toggleConversation = (requestId: string) => {
    if (expandedConversation === requestId) {
      setExpandedConversation(null);
    } else {
      setExpandedConversation(requestId);
      if (!conversationMessages[requestId]) {
        loadConversation(requestId);
      }
    }
  };

  const handleSendMessage = async (requestId: string) => {
    const msg = newMessage[requestId]?.trim();
    if (!msg) return;
    try {
      setSendingMessage(requestId);
      await api.post(`/discount-requests/${requestId}/conversation`, { message: msg });
      setNewMessage(prev => ({ ...prev, [requestId]: '' }));
      loadConversation(requestId);
    } catch {
      alert('Failed to send message');
    } finally {
      setSendingMessage(null);
    }
  };

  const getDocumentLink = (req: DiscountRequest) => {
    if (req.proposalId) return { label: 'View Proposal', path: `/proposals/${req.proposalId}/edit` };
    if (req.salesOrderId) return { label: 'View Sales Order', path: `/sales-orders/${req.salesOrderId}` };
    if (req.invoiceId) return { label: 'View Invoice', path: `/invoices/${req.invoiceId}` };
    return null;
  };

  const handleAction = async (id: string, action: 'approve' | 'reject', remarks?: string) => {
    try {
      setActionLoading(id);
      await api.post(`/discount-requests/${id}/${action}`, { remarks });
      setRemarksModal(null);
      setRemarksText('');
      fetchRequests();
    } catch {
      alert(`Failed to ${action} request`);
    } finally {
      setActionLoading(null);
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700"><Clock size={12} /> Pending</span>;
      case 'approved': return <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700"><Check size={12} /> Approved</span>;
      case 'rejected': return <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700"><X size={12} /> Rejected</span>;
      default: return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">{status}</span>;
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Discount Requests</h1>
            <p className="mt-1 text-sm text-gray-500">Review and approve discount requests from your team</p>
          </div>
          <div className="flex items-center gap-2">
            {['pending', 'approved', 'rejected', ''].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  filter === f
                    ? 'bg-[var(--ink-0)] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f || 'All'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Scope filter */}
          <div className="flex items-center gap-2">
            <Tag size={14} className="text-gray-400" />
            <span className="text-xs font-medium text-gray-500">Scope:</span>
            {['', 'product', 'document'].map(s => (
              <button
                key={s}
                onClick={() => setScopeFilter(s)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  scopeFilter === s
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {s === '' ? 'All' : s === 'product' ? 'Product' : 'Document'}
              </button>
            ))}
          </div>
          {/* Document type filter */}
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={14} className="text-gray-400" />
            <span className="text-xs font-medium text-gray-500">Type:</span>
            {['', 'proposal', 'sales_order', 'invoice'].map(dt => (
              <button
                key={dt}
                onClick={() => setDocTypeFilter(dt)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  docTypeFilter === dt
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {dt === '' ? 'All' : dt === 'proposal' ? 'Proposal' : dt === 'sales_order' ? 'Sales Order' : 'Invoice'}
              </button>
            ))}
          </div>
          {/* Customer type filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">Customer:</span>
            {['', 'b2b', 'b2c'].map(ct => (
              <button
                key={ct}
                onClick={() => setCustomerTypeFilter(ct)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  customerTypeFilter === ct
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {ct === '' ? 'All' : ct.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-gray-400">Loading...</div>
      ) : requests.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center text-gray-400">
          <Percent size={48} className="mb-2 opacity-30" />
          <p>No {filter} discount requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map(req => (
            <div key={req.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                {/* Left: Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    {statusBadge(req.status)}
                    {req.discountScope && (
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        req.discountScope === 'product' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        <Tag size={10} />
                        {req.discountScope === 'product' ? 'Product' : 'Document'}
                      </span>
                    )}
                    {req.documentType && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                        <FileSpreadsheet size={10} />
                        {req.documentType === 'proposal' ? 'Proposal' : req.documentType === 'sales_order' ? 'SO' : 'Invoice'}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">{new Date(req.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>

                  {/* Product & Discount */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {req.itemDescription || req.product?.name || 'Item'}
                    {req.product?.sku && <span className="ml-2 text-sm font-normal text-gray-400">({req.product.sku})</span>}
                  </h3>

                  <div className="flex flex-wrap gap-4 text-sm mt-2">
                    {/* Discount info */}
                    <div className="flex items-center gap-1.5">
                      <Percent size={14} className="text-red-500" />
                      <span className="font-medium text-red-600">{Number(req.requestedPct)}% requested</span>
                      {req.maxDiscountPct !== null && (
                        <span className="text-gray-400">(max {Number(req.maxDiscountPct)}%)</span>
                      )}
                    </div>

                    {/* Amount */}
                    {req.requestedAmt !== null && (
                      <div className="flex items-center gap-1.5">
                        <IndianRupee size={14} className="text-amber-500" />
                        <span className="font-medium text-amber-600">{formatINR(Number(req.requestedAmt))} discount</span>
                      </div>
                    )}

                    {/* Line item rate x qty */}
                    {req.itemRate && req.itemQuantity && (
                      <div className="text-gray-500">
                        Rate: {formatINR(Number(req.itemRate))} x {Number(req.itemQuantity)} qty
                      </div>
                    )}
                  </div>

                  {/* Deal context */}
                  {(req.dealTitle || req.customerName || req.dealValue) && (
                    <div className="mt-3 rounded-lg bg-gray-50 p-3">
                      <div className="flex flex-wrap gap-4 text-sm">
                        {req.dealTitle && (
                          <div className="flex items-center gap-1.5">
                            <Briefcase size={14} className="text-blue-500" />
                            <span className="text-gray-700">{req.dealTitle}</span>
                          </div>
                        )}
                        {req.customerName && (
                          <div className="flex items-center gap-1.5">
                            <User size={14} className="text-purple-500" />
                            <span className="text-gray-700">{req.customerName}</span>
                            {(req as any).deal?.contact?.customerType && (
                              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${(req as any).deal.contact.customerType === 'b2b' ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                                {(req as any).deal.contact.customerType === 'b2b' ? 'B2B' : 'B2C'}
                              </span>
                            )}
                          </div>
                        )}
                        {req.dealValue && (
                          <div className="flex items-center gap-1.5">
                            <IndianRupee size={14} className="text-green-500" />
                            <span className="font-medium text-green-700">Deal: {formatINR(Number(req.dealValue))}</span>
                          </div>
                        )}
                      </div>

                      {/* Deal activities (lead history) */}
                      {req.deal?.activities && req.deal.activities.length > 0 && (
                        <div className="mt-2 border-t border-gray-200 pt-2">
                          <p className="text-xs font-medium text-gray-500 mb-1">Recent Activities:</p>
                          {req.deal.activities.map(act => (
                            <div key={act.id} className="text-xs text-gray-500">
                              {act.title} — {new Date(act.createdAt).toLocaleDateString('en-IN')}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reason */}
                  {req.reason && (
                    <div className="mt-2 flex items-start gap-1.5 text-sm">
                      <FileText size={14} className="text-gray-400 mt-0.5 shrink-0" />
                      <span className="text-gray-600">{req.reason}</span>
                    </div>
                  )}

                  {/* Requested by */}
                  <div className="mt-2 text-xs text-gray-400">
                    Requested by {req.requestedBy.firstName} {req.requestedBy.lastName} ({req.requestedBy.email})
                  </div>

                  {/* Approval/rejection info */}
                  {req.approvedBy && (req.status === 'approved' || req.status === 'rejected') && (
                    <div className="mt-2 flex items-center gap-1.5 text-sm">
                      <User size={14} className={req.status === 'approved' ? 'text-green-500' : 'text-red-500'} />
                      <span className={req.status === 'approved' ? 'font-medium text-green-700' : 'font-medium text-red-700'}>
                        {req.status === 'approved' ? 'Approved' : 'Rejected'} by {req.approvedBy.firstName} {req.approvedBy.lastName}
                      </span>
                      {req.approvedAt && (
                        <span className="text-xs text-gray-400">
                          on {new Date(req.approvedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      {req.rejectedAt && (
                        <span className="text-xs text-gray-400">
                          on {new Date(req.rejectedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  )}
                  {req.remarks && (
                    <div className="mt-2 rounded bg-blue-50 px-3 py-1.5 text-sm text-blue-700">
                      <span className="font-medium">Remarks:</span> {req.remarks}
                    </div>
                  )}

                  {/* Document link */}
                  {(() => {
                    const docLink = getDocumentLink(req);
                    return docLink ? (
                      <button
                        onClick={() => navigate(docLink.path)}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--ink-0)] hover:text-[var(--ink-0)] hover:underline"
                      >
                        <FileText size={12} />
                        {docLink.label}
                      </button>
                    ) : null;
                  })()}
                </div>

                {/* Right: Actions */}
                {req.status === 'pending' && (
                  <div className="flex shrink-0 flex-col gap-2">
                    <button
                      onClick={() => { setRemarksModal({ id: req.id, action: 'approve' }); setRemarksText(''); }}
                      disabled={actionLoading === req.id}
                      className="flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      <Check size={14} /> Approve
                    </button>
                    <button
                      onClick={() => { setRemarksModal({ id: req.id, action: 'reject' }); setRemarksText(''); }}
                      disabled={actionLoading === req.id}
                      className="flex items-center gap-1.5 rounded-lg bg-red-50 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-100 disabled:opacity-50"
                    >
                      <X size={14} /> Reject
                    </button>
                  </div>
                )}
              </div>
              {/* Conversation Thread */}
              <div className="mt-3 border-t border-gray-100 pt-3">
                <button
                  onClick={() => toggleConversation(req.id)}
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
                >
                  <MessageSquare size={14} />
                  Conversation
                  {expandedConversation === req.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {expandedConversation === req.id && (
                  <div className="mt-2 space-y-2">
                    {(conversationMessages[req.id] || []).length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No messages yet.</p>
                    ) : (
                      (conversationMessages[req.id] || []).map((msg, idx) => (
                        <div key={msg.id || idx} className="rounded-lg bg-gray-800/50 px-3 py-2">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-medium text-gray-700">{msg.userName}</span>
                            <span className="text-[10px] text-gray-400">{new Date(msg.timestamp || msg.createdAt || '').toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          <p className="text-sm text-gray-600">{msg.message}</p>
                        </div>
                      ))
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newMessage[req.id] || ''}
                        onChange={e => setNewMessage(prev => ({ ...prev, [req.id]: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && handleSendMessage(req.id)}
                        placeholder="Add a message..."
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                      />
                      <button
                        onClick={() => handleSendMessage(req.id)}
                        disabled={sendingMessage === req.id || !newMessage[req.id]?.trim()}
                        className="rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                      >
                        <Send size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Remarks Modal */}
      {remarksModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setRemarksModal(null)}>
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {remarksModal.action === 'approve' ? 'Approve' : 'Reject'} Discount Request
            </h3>
            <textarea
              value={remarksText}
              onChange={e => setRemarksText(e.target.value)}
              placeholder="Add remarks (optional)..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              rows={3}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setRemarksModal(null)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction(remarksModal.id, remarksModal.action, remarksText || undefined)}
                disabled={actionLoading === remarksModal.id}
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
                  remarksModal.action === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
              >
                {actionLoading === remarksModal.id ? 'Processing...' : remarksModal.action === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
